import * as monaco from 'monaco-editor'
import type { EditorAdapter, EditorPosition, EditorSelection, EditorOptions } from '../editor-adapter'
import EditorWorker from 'monaco-editor/esm/vs/editor/editor.worker?worker'
import JsonWorker from 'monaco-editor/esm/vs/language/json/json.worker?worker'
import CssWorker from 'monaco-editor/esm/vs/language/css/css.worker?worker'
import HtmlWorker from 'monaco-editor/esm/vs/language/html/html.worker?worker'
import TsWorker from 'monaco-editor/esm/vs/language/typescript/ts.worker?worker'

self.MonacoEnvironment = {
  getWorker(_workerId: string, label: string) {
    switch (label) {
      case 'json':
        return new JsonWorker()
      case 'css':
      case 'scss':
      case 'less':
        return new CssWorker()
      case 'html':
      case 'handlebars':
      case 'razor':
        return new HtmlWorker()
      case 'typescript':
      case 'javascript':
        return new TsWorker()
      default:
        return new EditorWorker()
    }
  },
}

export function createMonacoAdapter(container: HTMLElement, options: EditorOptions): Promise<EditorAdapter> {
  const changeListeners: Array<(value: string) => void> = []
  const selectionListeners: Array<(selection: EditorSelection) => void> = []

  return new Promise((resolve) => {
    const editor = monaco.editor.create(container, {
      value: options.value ?? '',
      language: options.language ?? 'markdown',
      theme: options.theme === 'dark' ? 'vs-dark' : 'vs',
      readOnly: options.readOnly ?? false,
      minimap: { enabled: options.minimap ?? true },
      wordWrap: options.wordWrap !== false ? 'on' : 'off',
      fontSize: options.fontSize ?? 14,
      fontFamily: "'JetBrains Mono', Consolas, Menlo, monospace",
      lineNumbersMinChars: 3,
      scrollBeyondLastLine: false,
      smoothScrolling: true,
      cursorBlinking: 'smooth',
      renderLineHighlight: 'all',
      automaticLayout: true,
      tabSize: options.tabSize ?? 4,
    })

    const model = editor.getModel()
    if (model && options.placeholder) {
      // Monaco doesn't support placeholder natively; skip
    }

    editor.onDidChangeModelContent(() => {
      const value = editor.getValue()
      changeListeners.forEach((l) => l(value))
    })

    editor.onDidChangeCursorSelection(() => {
      const sel = editor.getSelection()!
      const offsetStart = model?.getOffsetAt({ lineNumber: sel.startLineNumber, column: sel.startColumn }) ?? 0
      const offsetEnd = model?.getOffsetAt({ lineNumber: sel.endLineNumber, column: sel.endColumn }) ?? 0
      selectionListeners.forEach((l) => {
        l({
          start: { line: sel.startLineNumber, column: sel.startColumn, offset: offsetStart },
          end: { line: sel.endLineNumber, column: sel.endColumn, offset: offsetEnd },
          isEmpty: sel.isEmpty(),
          text: model?.getValueInRange(sel) ?? '',
        })
      })
    })

    const adapter: EditorAdapter = {
      getValue() { return editor.getValue() },

      setValue(value: string) { editor.setValue(value) },

      getSelection(): EditorSelection {
        const sel = editor.getSelection()!
        const offsetStart = model?.getOffsetAt({ lineNumber: sel.startLineNumber, column: sel.startColumn }) ?? 0
        const offsetEnd = model?.getOffsetAt({ lineNumber: sel.endLineNumber, column: sel.endColumn }) ?? 0
        return {
          start: { line: sel.startLineNumber, column: sel.startColumn, offset: offsetStart },
          end: { line: sel.endLineNumber, column: sel.endColumn, offset: offsetEnd },
          isEmpty: sel.isEmpty(),
          text: model?.getValueInRange(sel) ?? '',
        }
      },

      setSelection(start: EditorPosition, end?: EditorPosition) {
        const p1 = { lineNumber: start.line, column: Math.max(1, start.column) }
        const p2 = end ? { lineNumber: end.line, column: Math.max(1, end.column) } : p1
        editor.setSelection({ startLineNumber: p1.lineNumber, startColumn: p1.column, endLineNumber: p2.lineNumber, endColumn: p2.column })
        editor.focus()
      },

      replaceSelection(text: string) {
        editor.executeEdits('toolbar', [{ range: editor.getSelection()!, text, forceMoveMarkers: true }])
      },

      insertText(text: string) {
        const selection = editor.getSelection() || new monaco.Range(1, 1, 1, 1)
        editor.executeEdits('toolbar', [{ range: selection, text, forceMoveMarkers: true }])
      },

      wrapSelection(prefix: string, suffix: string) {
        const sel = editor.getSelection()!
        const selected = model?.getValueInRange(sel) ?? ''
        if (sel.isEmpty()) {
          editor.executeEdits('toolbar', [{ range: sel, text: prefix + selected + suffix, forceMoveMarkers: true }])
          editor.setPosition({ lineNumber: sel.startLineNumber, column: sel.startColumn + prefix.length })
        } else {
          editor.executeEdits('toolbar', [{ range: sel, text: prefix + selected + suffix, forceMoveMarkers: true }])
          if (model) {
            const newStart = model.getOffsetAt({ lineNumber: sel.startLineNumber, column: sel.startColumn }) + prefix.length
            const newEnd = model.getOffsetAt({ lineNumber: sel.endLineNumber, column: sel.endColumn }) + suffix.length
            const p1 = model.getPositionAt(newStart)
            const p2 = model.getPositionAt(newEnd)
            editor.setSelection({ startLineNumber: p1.lineNumber, startColumn: p1.column, endLineNumber: p2.lineNumber, endColumn: p2.column })
          }
        }
        editor.focus()
      },

      wrapLine(prefix: string, suffix: string) {
        const sel = editor.getSelection()!
        const startLine = sel.startLineNumber
        const endLine = sel.endLineNumber
        const ranges: monaco.IRange[] = []
        const texts: string[] = []
        for (let lineNumber = startLine; lineNumber <= endLine; lineNumber++) {
          const lineContent = model?.getLineContent(lineNumber) ?? ''
          const indent = /^\s*/.exec(lineContent)?.[0] ?? ''
          const range = { startLineNumber: lineNumber, startColumn: 1, endLineNumber: lineNumber, endColumn: model?.getLineMaxColumn(lineNumber) ?? 1 }
          ranges.push(range)
          texts.push(indent + prefix + lineContent.slice(indent.length) + suffix)
        }
        editor.executeEdits('toolbar', ranges.map((r, i) => ({ range: r, text: texts[i] })))
        editor.focus()
      },

      toggleLinePrefix(prefix: string) {
        const sel = editor.getSelection()!
        const startLine = sel.startLineNumber
        const endLine = sel.endLineNumber
        const statuses: boolean[] = []
        for (let lineNumber = startLine; lineNumber <= endLine; lineNumber++) {
          const lineContent = model?.getLineContent(lineNumber) ?? ''
          const indent = /^[ \t]*/.exec(lineContent)?.[0] ?? ''
          statuses.push(lineContent.slice(indent.length).startsWith(prefix))
        }
        const alreadyApplied = statuses.every(Boolean)
        const ranges: monaco.IRange[] = []
        const texts: string[] = []
        for (let lineNumber = startLine; lineNumber <= endLine; lineNumber++) {
          const lineContent = model?.getLineContent(lineNumber) ?? ''
          const indent = /^[ \t]*/.exec(lineContent)?.[0] ?? ''
          if (alreadyApplied) {
            ranges.push({ startLineNumber: lineNumber, startColumn: 1, endLineNumber: lineNumber, endColumn: indent.length + prefix.length + 1 })
            texts.push(indent)
          } else {
            ranges.push({ startLineNumber: lineNumber, startColumn: indent.length + 1, endLineNumber: lineNumber, endColumn: indent.length + 1 })
            texts.push(prefix)
          }
        }
        editor.executeEdits('toolbar', ranges.map((r, i) => ({ range: r, text: texts[i] })))
        editor.focus()
      },

      indentLines() {
        const sel = editor.getSelection()!
        for (let lineNumber = sel.startLineNumber; lineNumber <= sel.endLineNumber; lineNumber++) {
          const indent = /^\s*/.exec(model?.getLineContent(lineNumber) ?? '')?.[0] ?? ''
          editor.executeEdits('toolbar', [{
            range: { startLineNumber: lineNumber, startColumn: 1, endLineNumber: lineNumber, endColumn: indent.length + 1 },
            text: indent + '  ',
          }])
        }
        editor.focus()
      },

      outdentLines() {
        const sel = editor.getSelection()!
        for (let lineNumber = sel.startLineNumber; lineNumber <= sel.endLineNumber; lineNumber++) {
          const lineContent = model?.getLineContent(lineNumber) ?? ''
          const m = /^ {0,4}|\t/.exec(lineContent)
          if (m) {
            editor.executeEdits('toolbar', [{
              range: { startLineNumber: lineNumber, startColumn: 1, endLineNumber: lineNumber, endColumn: m[0].length + 1 },
              text: '',
            }])
          }
        }
        editor.focus()
      },

      insertLink() {
        const sel = editor.getSelection()!
        const selected = model?.getValueInRange(sel) ?? ''
        const label = selected || '链接文字'
        editor.executeEdits('toolbar', [{ range: sel, text: `[${label}](https://)` }])
        editor.focus()
      },

      insertImage() {
        const sel = editor.getSelection()!
        const selected = model?.getValueInRange(sel) ?? ''
        const label = selected || '图片描述'
        editor.executeEdits('toolbar', [{ range: sel, text: `![${label}](https://)` }])
        editor.focus()
      },

      insertTable() {
        const pos = editor.getPosition()!
        const table = '| �? | �? | �? |\n| --- | --- | --- |\n| 单元�?| 单元�?| 单元�?|\n| 单元�?| 单元�?| 单元�?|'
        editor.executeEdits('toolbar', [{ range: { startLineNumber: pos.lineNumber, startColumn: pos.column, endLineNumber: pos.lineNumber, endColumn: pos.column }, text: table }])
        editor.focus()
      },

      insertHorizontalRule() {
        const pos = editor.getPosition()!
        editor.executeEdits('toolbar', [{ range: { startLineNumber: pos.lineNumber, startColumn: pos.column, endLineNumber: pos.lineNumber, endColumn: pos.column }, text: '\n---\n' }])
        editor.focus()
      },

      insertFootnote() {
        const count = editor.getValue().match(/\[\^(\d+)\]/g)?.length ?? 0
        const n = count + 1
        const pos = editor.getPosition()!
        editor.executeEdits('toolbar', [{ range: { startLineNumber: pos.lineNumber, startColumn: pos.column, endLineNumber: pos.lineNumber, endColumn: pos.column }, text: ` [^${n}]` }])
        editor.focus()
      },

      undo() { editor.trigger('toolbar', 'undo', null) },
      redo() { editor.trigger('toolbar', 'redo', null) },

      canUndo(): boolean { return (model as any)?.canUndo() ?? false },
      canRedo(): boolean { return (model as any)?.canRedo() ?? false },

      focus() { editor.focus() },

      onChange(callback: (value: string) => void) {
        changeListeners.push(callback)
        return () => { const i = changeListeners.indexOf(callback); if (i >= 0) changeListeners.splice(i, 1) }
      },

      onSelectionChange(callback: (selection: EditorSelection) => void) {
        selectionListeners.push(callback)
        return () => { const i = selectionListeners.indexOf(callback); if (i >= 0) selectionListeners.splice(i, 1) }
      },

      getLineCount() { return model?.getLineCount() ?? 0 },
      getLineContent(line: number) { return model?.getLineContent(line) ?? '' },
      setLineContent(line: number, content: string) {
        editor.executeEdits('toolbar', [{
          range: { startLineNumber: line, startColumn: 1, endLineNumber: line, endColumn: (model?.getLineMaxColumn(line) ?? 1) },
          text: content,
        }])
      },

      isSelectionWrapped(prefix: string, suffix: string) {
        const sel = editor.getSelection()!
        const text = model?.getValueInRange(sel) ?? ''
        return text.startsWith(prefix) && text.endsWith(suffix)
      },

      dispose() {
        changeListeners.length = 0
        selectionListeners.length = 0
        editor.dispose()
      },
    }

    resolve(adapter)
  })
}